// Ludo Learning — Engine: path coords, special squares, bot AI, capture & home logic.
// Classic 4-player Ludo on a 15x15 board.
// 52 outer cells + 5 home column cells per player + 1 center = 57 path positions per token.
// You = Yellow (BL). Opponents = Green (TL), Red (TR), Blue (BR), assigned to bots.
// User chooses how many AI bots (1, 2 or 3) — the rest are inactive (board still shows 4 bases for classic look).

// ---------- PATH COORDS (Classic Ludo King layout) ----------
// 15x15 board. Bases at 4 corners (6x6 each). Cross arms 3-cells wide.
// Yellow base = bottom-left. Yellow exits adjacent to its base's RIGHT side and travels
// CLOCKWISE around the outer track for 51 cells before turning into its home column.
//
// Outer path 52 cells (indices 0..51):
//   0      = YELLOW START [13,6]  — right of yellow base, bottom arm left column
//   13     = GREEN  START [6,1]   — bottom of green base, left arm top row
//   26     = RED    START [1,8]   — left of red base, top arm right column
//   39     = BLUE   START [8,13]  — top of blue base, right arm bottom row
//
// Each color enters its home column at outer index (start + 50) % 52 — i.e., 50 cells
// after exit, then turns INWARD on the 51st step into home column cell 0.
export const OUTER_PATH = [
    // 0-4: Bottom arm LEFT side going UP  (yellow exits here)
    [13, 6], [12, 6], [11, 6], [10, 6], [9, 6],
    // 5-10: Left arm BOTTOM edge going WEST
    [8, 5], [8, 4], [8, 3], [8, 2], [8, 1], [8, 0],
    // 11-12: Left arm TIP going UP
    [7, 0], [6, 0],
    // 13-17: Left arm TOP edge going EAST  (green exits here)
    [6, 1], [6, 2], [6, 3], [6, 4], [6, 5],
    // 18-22: Top arm LEFT side going UP
    [5, 6], [4, 6], [3, 6], [2, 6], [1, 6],
    // 23-25: Top tip
    [0, 6], [0, 7], [0, 8],
    // 26-30: Top arm RIGHT side going DOWN  (red exits here)
    [1, 8], [2, 8], [3, 8], [4, 8], [5, 8],
    // 31-36: Right arm TOP edge going EAST
    [6, 9], [6, 10], [6, 11], [6, 12], [6, 13], [6, 14],
    // 37-38: Right arm TIP going DOWN
    [7, 14], [8, 14],
    // 39-43: Right arm BOTTOM edge going WEST  (blue exits here)
    [8, 13], [8, 12], [8, 11], [8, 10], [8, 9],
    // 44-48: Bottom arm RIGHT side going DOWN
    [9, 8], [10, 8], [11, 8], [12, 8], [13, 8],
    // 49-51: Bottom tip
    [14, 8], [14, 7], [14, 6],
];

// Home columns — 5 cells each. Each color turns into home column from the outer cell at
// (start + 50) % 52, then walks 5 cells into HOME[0..4], then arrives at CENTER.
//
//   yellow home entry = OUTER[50] = [14,7]  → YELLOW_HOME[0] = [13,7]  (bottom arm middle, going UP)
//   green  home entry = OUTER[11] = [7,0]   → GREEN_HOME[0]  = [7,1]   (left arm middle, going EAST)
//   red    home entry = OUTER[24] = [0,7]   → RED_HOME[0]    = [1,7]   (top arm middle, going DOWN)
//   blue   home entry = OUTER[37] = [7,14]  → BLUE_HOME[0]   = [7,13]  (right arm middle, going WEST)
export const YELLOW_HOME = [[13, 7], [12, 7], [11, 7], [10, 7], [9, 7]];   // S→N (yellow approaches center from SOUTH)
export const GREEN_HOME  = [[7, 1],  [7, 2],  [7, 3],  [7, 4],  [7, 5]];   // W→E (green approaches from WEST)
export const RED_HOME    = [[1, 7],  [2, 7],  [3, 7],  [4, 7],  [5, 7]];   // N→S (red approaches from NORTH)
export const BLUE_HOME   = [[7, 13], [7, 12], [7, 11], [7, 10], [7, 9]];   // E→W (blue approaches from EAST)

// Center
export const CENTER = [7, 7];

export const OUTER_STEPS = 51;
export const HOME_STEPS = 5;
export const TOTAL_STEPS = OUTER_STEPS + HOME_STEPS + 1; // 57; pos==57 means HOME

// ---------- COLORS ----------
export const ALL_COLORS = ['yellow', 'green', 'red', 'blue'];
export const COLOR_START_OUTER = { yellow: 0, green: 13, red: 26, blue: 39 };
export const COLOR_HOME = {
    yellow: YELLOW_HOME, green: GREEN_HOME, red: RED_HOME, blue: BLUE_HOME,
};

// ---------- SPECIAL SQUARES ----------
// Mapped to outer cell indices (0..51).
// All 4 color starts are safe by default. Plus extra mid-quadrant safes (classic Ludo "stars").
export const SQUARES = {
    safe:     [0, 8, 13, 21, 26, 34, 39, 47],
    star:     [3, 18, 25, 38],                  // ⭐ "Did you know?" learning star (auto +10)
    question: [5, 10, 15, 20, 30, 35, 40, 45],   // ❓ MCQ (+20 correct, -4 wrong)
    hazard:   [7, 22, 33, 49],                   // ⚡ Safety hazard scenario (+30 correct, -6 wrong)
    boss:     [12, 31, 42],                      // 🏆 Boss round (3 rapid questions)
    tip:      [2, 9, 17, 28, 36, 43],            // 💡 Learning tip (auto +5)
    shortcut: [11, 37],                          // 🎯 Shortcut (correct = +10 cells)
};

export function squareType(outerIdx) {
    for (const [t, arr] of Object.entries(SQUARES)) if (arr.includes(outerIdx)) return t;
    return 'plain';
}

// ---------- HOME BASE (parking) COORDS ----------
// 4 token slots per color, inside the 6x6 home base quadrant (inner 4 visible positions).
export const BASE_SLOTS = {
    yellow: [[10, 1], [10, 4], [13, 1], [13, 4]],  // BL
    green:  [[1, 1],  [1, 4],  [4, 1],  [4, 4]],   // TL
    red:    [[1, 10], [1, 13], [4, 10], [4, 13]],  // TR
    blue:   [[10, 10],[10, 13],[13, 10],[13, 13]], // BR
};

// ---------- TOKEN POSITION → COORD ----------
// position: 0 = in base, 1..51 = outer track, 52..56 = home column, 57 = CENTER/win
export function tokenCoord(color, position) {
    if (position === 0) return null;
    if (position === 57) return CENTER;
    if (position >= 1 && position <= 51) {
        const startOuter = COLOR_START_OUTER[color];
        const idx = (startOuter + position - 1) % 52;
        return OUTER_PATH[idx];
    }
    return COLOR_HOME[color][position - 52];
}

export function tokenOuterIndex(color, position) {
    if (position < 1 || position > 51) return null;
    return (COLOR_START_OUTER[color] + position - 1) % 52;
}

// ---------- MOVE VALIDATION ----------
export function legalMoves(color, tokens, dice) {
    const moves = [];
    tokens.forEach((pos, i) => {
        if (pos === 0) {
            if (dice === 6) moves.push(i);
        } else if (pos < 57) {
            if (pos + dice <= 57) moves.push(i);
        }
    });
    return moves;
}

// ---------- CAPTURE ----------
// Returns { capturedColor, capturedIndices: [...] }[] for all opponents of `color` whose
// tokens are on the SAME outer cell as `newPos` and that cell is NOT a safe square.
// allTokens = { yellow: [...], green: [...], red: [...], blue: [...] }
// activeColors = subset that are in play.
export function capturesAt(color, newPos, allTokens, activeColors) {
    if (newPos < 1 || newPos > 51) return [];
    const newOuter = tokenOuterIndex(color, newPos);
    if (SQUARES.safe.includes(newOuter)) return [];
    const out = [];
    activeColors.forEach((oppColor) => {
        if (oppColor === color) return;
        const indices = [];
        (allTokens[oppColor] || []).forEach((opPos, i) => {
            if (opPos < 1 || opPos > 51) return;
            if (tokenOuterIndex(oppColor, opPos) === newOuter) indices.push(i);
        });
        if (indices.length) out.push({ capturedColor: oppColor, capturedIndices: indices });
    });
    return out;
}

// ---------- BOT AI ----------
// Strategy per bot:
// 1. Capture if possible.
// 2. Release a token (rolled 6) with ~60% probability if any in base.
// 3. Advance the token closest to home that has a legal move.
export function botPickToken(color, myTokens, allTokens, activeColors, dice) {
    const legal = legalMoves(color, myTokens, dice);
    if (!legal.length) return null;
    // Capture opportunity
    for (const i of legal) {
        const newPos = myTokens[i] === 0 ? 1 : myTokens[i] + dice;
        const caps = capturesAt(color, newPos, allTokens, activeColors);
        if (caps.length) return i;
    }
    if (dice === 6) {
        const inBase = legal.filter((i) => myTokens[i] === 0);
        if (inBase.length && Math.random() < 0.6) return inBase[0];
    }
    const advanceable = legal.filter((i) => myTokens[i] > 0).sort((a, b) => myTokens[b] - myTokens[a]);
    return advanceable[0] ?? legal[0];
}

export function botAnswersCorrectly() { return Math.random() < 0.7; }

// ---------- FALLBACK QUESTIONS ----------
export const FALLBACK_QUESTIONS = [
    { question: 'Which Indian Standard governs PVC insulated domestic wires?', options: ['IS 1554', 'IS 694', 'IS 7098', 'IS 9968'], correct: 'IS 694', explanation: 'IS 694 covers PVC insulated cables up to 1100V — the standard for house wiring.' },
    { question: 'What does FRLS stand for?', options: ['Fire-Resistant Low Surge', 'Flame Retardant Low Smoke', 'Fully Recyclable Lead-free', 'Flexible Rated Long Service'], correct: 'Flame Retardant Low Smoke', explanation: 'FRLS wires release minimal smoke and resist flame spread — critical for high-rise buildings.' },
    { question: 'Earth wire colour as per IS 11353?', options: ['Red', 'Black', 'Green / Green-Yellow', 'Blue'], correct: 'Green / Green-Yellow', explanation: 'Green or green-yellow striped is the universal earth identifier.' },
    { question: 'XLPE vs PVC — XLPE allows higher?', options: ['Colour options', 'Operating temperature', 'Flexibility', 'Cost'], correct: 'Operating temperature', explanation: 'XLPE handles 90°C continuous vs PVC 70°C — better for high-load cables.' },
    { question: 'Mandatory PPE for live LV panel work?', options: ['Sunglasses', 'Insulated gloves & arc shield', 'T-shirt', 'Slippers'], correct: 'Insulated gloves & arc shield', explanation: 'Class-0 insulated gloves + arc-rated face shield are mandatory for live work.' },
    { question: 'Cable drum storage best practice?', options: ['Flat on side', 'Upright on flanges, dry area', '10 drums stacked', 'Outdoors uncovered'], correct: 'Upright on flanges, dry area', explanation: 'Drums must stand on flanges in covered, dry storage to prevent moisture ingress.' },
    { question: 'Recommended cable size for a 16 A AC point?', options: ['0.5 sq.mm', '1.0 sq.mm', '2.5 sq.mm', '4.0 sq.mm'], correct: '4.0 sq.mm', explanation: '4 sq.mm copper for 16A circuits — never undersized to avoid overheating.' },
    { question: 'LOTO is used to?', options: ['Label cables', 'Lock & tag de-energised equipment', 'Test load', 'Time outages'], correct: 'Lock & tag de-energised equipment', explanation: 'Lockout-Tagout — 6-step procedure to prevent accidental re-energisation.' },
    { question: 'Conductor material for premium house wires?', options: ['Aluminium', '99.97% electrolytic copper', 'Steel', 'Brass'], correct: '99.97% electrolytic copper', explanation: 'Electrolytic-grade copper offers lowest resistivity and best conductivity.' },
    { question: 'Minimum insulation resistance for new house wiring?', options: ['0.1 MΩ', '0.5 MΩ', '1 MΩ', '50 MΩ'], correct: '1 MΩ', explanation: 'IS code requires ≥1 MΩ measured with 500V megger between phase & earth.' },
    { question: 'XLPE LT cable maximum operating voltage?', options: ['440V', '1.1 kV', '11 kV', '33 kV'], correct: '1.1 kV', explanation: 'LT (low tension) XLPE is rated up to 1.1 kV; HT versions go 11 kV+.' },
    { question: 'Counterfeit cable risk?', options: ['Higher cost', 'Lower copper purity → overheating', 'Bright colours', 'Longer life'], correct: 'Lower copper purity → overheating', explanation: 'Counterfeits often use copper-clad aluminium — high resistance & fire risk.' },
];

export const LEARNING_TIPS = [
    { title: 'FR vs FRLS vs FR-LSH', body: 'FR retards flame. FRLS adds low smoke. FR-LSH is low smoke + halogen-free — safest for hospitals & data centres.' },
    { title: 'IS 694 in 30 seconds', body: 'IS 694 covers single-core PVC wires up to 1100V — the backbone standard for Indian house wiring.' },
    { title: 'Cable Ampacity Rule', body: '2.5 sq.mm copper carries ~24A in open air, ~18A in conduit. Always derate for grouping & ambient temperature.' },
    { title: 'Insulation Resistance Test', body: 'Use a 500V megger. Reading must be ≥1 MΩ between phase-neutral, phase-earth and neutral-earth.' },
    { title: 'Drum Storage Tip', body: 'Always store drums upright on flanges in dry covered space. Lying flat can crush the inner layers.' },
    { title: 'Counterfeit Detection', body: 'Test conductor with a magnet — pure copper is non-magnetic. CCA (copper-clad aluminium) will not be attracted but feels lighter & cuts unevenly.' },
    { title: 'LOTO 6 Steps', body: '1) Notify, 2) Identify energy sources, 3) Isolate, 4) Lockout & Tagout, 5) Verify zero energy, 6) Test before touch.' },
    { title: 'Phase Colour Code (India)', body: 'Red = R-phase, Yellow = Y-phase, Blue = B-phase, Black = Neutral, Green/G-Y = Earth.' },
];

export const TOPIC_PRESETS = [
    'Propulse FR/FRLS house wiring safety',
    'XLPE LT & HT power cables',
    'IS 694 / IS 7098 / IS 1554 standards',
    'Electrical site safety, PPE & LOTO',
    'Cable drum handling & counterfeit detection',
    'Channel partner billing & inventory ops',
    'Electrician troubleshooting basics',
];

// ---------- BOT COUNT → ACTIVE COLORS ----------
// Player is always 'yellow'. Active bot colors chosen for visual balance:
//   1 bot  → opposite diagonal (Red TR)
//   2 bots → diagonal + adjacent (Red TR + Green TL)
//   3 bots → full classic (Green, Red, Blue)
// Turn order is always clockwise on the outer track starting from Yellow.
export function activeColorsFor(numBots) {
    const order = ['yellow', 'green', 'red', 'blue']; // clockwise on board
    if (numBots === 1) return ['yellow', 'red'];
    if (numBots === 2) return ['yellow', 'green', 'red'];
    return order; // 3 bots = all 4
}

// Bot colors with display labels and bot names (for log readability)
export const COLOR_LABELS = {
    yellow: { name: 'You',      emoji: '🟡' },
    green:  { name: 'Bot Green', emoji: '🟢' },
    red:    { name: 'Bot Red',   emoji: '🔴' },
    blue:   { name: 'Bot Blue',  emoji: '🔵' },
};
